import { memo, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { FormContainer } from 'components/layout';
import { Checkbox, ChipInput, SelectableCreativesGrid } from 'components/common';
import type { CreativeEntry } from 'types/shared';
import datadogActions from 'lib/datadog';
import { useLocationParams } from 'hooks';
import { CREATIVES_LIMIT, Network } from '../../../constants';
import usePinterestCreativesData from '../hooks/usePinterestCreativesData';

const COLUMNS: GridColDef<CreativeEntry>[] = [
  { field: 'creative_id', headerName: 'Creative ID', width: 110 },
  { field: 'name', headerName: 'Name', width: 450 },
  { field: 'app', headerName: 'App', width: 90 },
  { field: 'creative_type', headerName: 'Type', width: 100 },
  { field: 'created', headerName: 'Created', width: 120 },
  {
    field: 'tpc_finish_date',
    headerName: 'TPC date',
    width: 120,
    valueGetter: (value: string | null | undefined) => value ?? '',
  },
];

const ChooseCreatives = memo(() => {
  const {
    trigger,
    formState: { errors },
  } = useFormContext();
  const creativesError = errors.selectedCreatives?.message as string | undefined;

  const { rows, totalCount, selectedCreatives, setSelection, isLoading, isError, error } =
    usePinterestCreativesData();
  const filterByName = useWatch({ name: 'filterCreativeByName' }) as string[] | undefined;
  const { setFlatCreatives: setFlatCreativesParam, setFilterCreativesByName: setFilterCreativesByNameParam } =
    useLocationParams();

  const hasWrittenCreativesRef = useRef(false);

  useEffect(() => {
    // First paint can still have an empty selection while a deep-linked
    // `creatives` param is being restored; writing it would clear the URL.
    if (!selectedCreatives.length && !hasWrittenCreativesRef.current) return;

    hasWrittenCreativesRef.current = true;
    setFlatCreativesParam(selectedCreatives);
  }, [selectedCreatives, setFlatCreativesParam]);

  useEffect(() => {
    setFilterCreativesByNameParam(filterByName ?? []);
  }, [filterByName, setFilterCreativesByNameParam]);

  useEffect(() => {
    void trigger('selectedCreatives');
  }, [trigger]);

  useEffect(() => {
    if (!isError || !error) return;

    datadogActions.trackDatadogError(error as Error, {
      source: 'pinterest_choose_creatives_query',
      network: Network.PINTEREST,
    });
  }, [isError, error]);

  return (
    <FormContainer
      title="Select creatives"
      error={creativesError}
      sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2, height: '100%' }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%' }}>
        <Box sx={{ flex: 1, maxWidth: '50%' }}>
          <ChipInput name="filterCreativeByName" inputLabel="Search by name" />
        </Box>
        <Checkbox
          dataTestId="selectedCreativesOnly"
          name="selectedCreativesOnly"
          label="Selected only"
          controlLabelSx={{ whiteSpace: 'nowrap' }}
        />
      </Box>
      {creativesError ? (
        <Typography variant="caption" color="error" role="alert">
          {creativesError}
        </Typography>
      ) : null}

      <SelectableCreativesGrid
        rows={rows}
        columns={COLUMNS}
        selectedIds={selectedCreatives}
        onSelectionChange={setSelection}
        limit={CREATIVES_LIMIT}
        isLoading={isLoading}
        totalCount={totalCount}
      />
    </FormContainer>
  );
});

ChooseCreatives.displayName = 'ChooseCreatives';

export default ChooseCreatives;
